/**
 * changes to this file will affect both request access and waiting room.
 */
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { User } from 'src/app/models/user';
import { AclService } from 'src/app/services/acl.service';
import {
  NotificationActionButton,
  NotificationIconConfig,
  NotificationIconTypes,
  NotificationToasterService,
  NotificationType,
  V2NotificationData,
  V2NotificationDataBuilder,
} from 'src/app/services/notification-toaster.service';
import { SessionSharedDataService } from 'src/app/services/session-shared-data.service';
import { PanelView } from 'src/app/sessions/panel/panel.component';
import { Section } from 'src/app/sessions/panel/participants-manager/participants-manager.component';
import { ToasterPopupStyle } from 'src/app/ui/notification-toaster/custom-notification-toastr/custom-notification-toastr.component';
import { IconBackground } from 'src/app/ui/notification-toaster/icon-message-toaster-element/icon-message-toaster-element.component';
import { AudioChimes, AudioService } from 'src/app/services/audio.service';
import { CustomerSupportService } from 'src/app/services/customer-support.service';
import { UserService } from 'src/app/services/user.service';
import { isObserverTabId } from 'src/app/services/observer-mode.utils';
import { IconTypes } from '../../../../standalones/components/pencil-icon/pencil-icon.component';
@Injectable()
export abstract class RequestAccessNotificationBaseService {
  protected _requesterApproved$: Subject<User> = new Subject();
  protected _requesterRejected$: Subject<User> = new Subject();
  protected _approveAll$: Subject<void> = new Subject<void>();

  protected activeNotificationData?: V2NotificationData;
  protected isNotificationActive = false;

  protected abstract notificationCode: string;
  protected abstract warningCode: string;
  protected abstract errorCode: string;
  protected abstract acceptButtontext: string;
  protected abstract acceptAllButtontext: string;
  protected abstract singleRequestNotificationText: string;
  protected abstract multiRequestsNotificationText: string;
  protected abstract msg: string;
  protected abstract defaultNotificationIconName: string;

  protected _startShowingNotifications: boolean = false;

  protected _lastRequestsNum: number = -1;
  protected _lastRequesterID: string = '';

  private _approveButton!: NotificationActionButton;
  private _rejectButton!: NotificationActionButton;
  private _approveAllButton!: NotificationActionButton;
  private _reviewButtonSecondary!: NotificationActionButton;
  private multipleNotificationIcon!: NotificationIconConfig;
  private multipleNotificationTitle!: string;
  private _singleStyleUserNotificationIcon!: NotificationIconConfig;
  private _singleStyleUserNotificationTitle!: string;
  private _singleStyleGuestNotificationIcon!: NotificationIconConfig;
  private _singleStyleGuestNotificationTitle!: string;
  private _somethnigWentWrongNotificationData!: V2NotificationData;

  protected constructor(
    protected sessionSharedDataService: SessionSharedDataService,
    protected translateService: TranslateService,
    protected notificationToasterService: NotificationToasterService,
    protected toastr: ToastrService,
    protected aclService: AclService,
    protected audioService: AudioService,
    protected customerSupportService: CustomerSupportService,
    protected userService: UserService,
  ) {}

  public showNotificationIfThereAreRequests(requesters: User[]) {
    const accessRequestsNum = requesters.length;
    if (accessRequestsNum === 0 || !this._startShowingNotifications) {
      this.dismissRequestsNotification();
      return;
    }
    const onlyRequester = accessRequestsNum === 1 ? requesters[0] : undefined;

    if (this.isNotificationActive) {
      this.updateExistingNotificationIfThereAreRequests(requesters);
    } else {
      this.createNewNotification(accessRequestsNum, onlyRequester);
    }
    this.audioService.playAudio(AudioChimes.waitingRoomRequest);
  }

  protected createNewNotification(accessRequestsNum: number, requester?: User) {
    this.activeNotificationData = this.buildNotification(accessRequestsNum, requester);

    this.isNotificationActive = true;

    this.notificationToasterService.showNotification(
      this.activeNotificationData,
      () => (this.isNotificationActive = false),
    );
  }

  protected buildNotification(accessRequestsNum: number, requester?: User): V2NotificationData {
    const {
      secondaryActionButton,
      notificationTitle,
      notificationDescription,
      primaryActionButton,
      notificationIconConfig,
    } = this.getNotificationItems(accessRequestsNum, requester);

    const notificationData = new V2NotificationDataBuilder(this.notificationCode)
      .type(NotificationType.WARNING)
      .style(ToasterPopupStyle.WARN)
      .title(notificationTitle)
      .icon(notificationIconConfig)
      .actionButtonsConfig({
        primary: primaryActionButton,
        secondary: secondaryActionButton,
      })
      .showProgressBar(false)
      .dismissable(true)
      .priority(600);

    if (notificationDescription) {
      notificationData.description(notificationDescription);
    }

    return notificationData.build();
  }

  public updateExistingNotificationIfThereAreRequests(requesters: User[]) {
    const accessRequestsNum = requesters.length;
    if (accessRequestsNum === 0 || !this._startShowingNotifications) {
      this.dismissRequestsNotification();
      return;
    }

    const onlyRequester = requesters.length === 1 ? requesters[0] : undefined;

    if (
      !this.activeNotificationData ||
      !this.isNotificationActive ||
      this.isSameNotificationShown(accessRequestsNum, onlyRequester)
    ) {
      return;
    }
    const {
      notificationTitle,
      notificationIconConfig,
      primaryActionButton,
      secondaryActionButton,
      notificationDescription,
    } = this.getNotificationItems(accessRequestsNum, onlyRequester);

    this.activeNotificationData.title = notificationTitle;
    this.activeNotificationData.icon = notificationIconConfig;
    this.activeNotificationData.description = notificationDescription ?? '';
    if (this.isObserverModeActive()) {
      delete this.activeNotificationData.actionButtonsConfig;
    } else {
      this.activeNotificationData.actionButtonsConfig = {
        primary: primaryActionButton,
        secondary: secondaryActionButton,
      };
    }
  }

  private isObserverModeActive(): boolean {
    return isObserverTabId(this.userService.userUniqueHash);
  }

  private isSameNotificationShown(accessRequestsNum: number, requester?: User): boolean {
    return (
      accessRequestsNum === this._lastRequestsNum &&
      (accessRequestsNum > 1 || this._lastRequesterID === requester?._id)
    );
  }

  protected getNotificationItems(
    accessRequestsNum: number,
    requester?: User,
  ): AccessRequestsNotificationItems {
    this._lastRequestsNum = accessRequestsNum;
    this._lastRequesterID = requester?._id ?? '';

    if (accessRequestsNum === 1 && requester) {
      return this.getSingleRequestsNotificationItems(requester);
    }

    return this.getMultipleRequestsNotificationItems(accessRequestsNum);
  }

  private getMultipleRequestsNotificationItems(
    accessRequestsNum: number,
  ): AccessRequestsNotificationItems {
    this.adjustReviewButton(accessRequestsNum);
    this.adjustApproveAllButton();
    this.adjustMultipleTitleElement(accessRequestsNum);

    return {
      secondaryActionButton: this._reviewButtonSecondary,
      primaryActionButton: this._approveAllButton,
      notificationTitle: this.multipleNotificationTitle,
      notificationIconConfig: this.multipleNotificationIcon,
      notificationDescription: this.msg,
    };
  }

  private adjustReviewButton(accessRequestsNum: number) {
    if (!this._reviewButtonSecondary) {
      this._reviewButtonSecondary = {
        text: `${this.translateService.instant('Review')} (${accessRequestsNum})`,
        icon: 'access_requests_forward_arrow_black',
        iconConfig: {
          type: IconTypes.svgIcon,
        },
        onClick: () => {
          this.goToAccessRequestsSection();
        },
        close: true,
      };
    } else {
      this._reviewButtonSecondary.text = `${this.translateService.instant(
        'Review',
      )} (${accessRequestsNum})`;
    }
  }

  private adjustApproveAllButton() {
    if (!this._approveAllButton) {
      this._approveAllButton = {
        text: this.acceptAllButtontext,
        icon: 'access_requests_approve_white',
        iconConfig: {
          type: IconTypes.svgIcon,
        },
        onClick: () => {
          this.approveAll();
        },
        close: true,
      };
    }
  }

  private adjustMultipleTitleElement(accessRequestsNum: number) {
    this.multipleNotificationTitle = `${accessRequestsNum} ${this.multiRequestsNotificationText}`;
    this.multipleNotificationIcon = {
      assetName: 'access_requests_clock_black',
      type: NotificationIconTypes.SVG_ICON,
      width: 18,
      height: 18,
      background: IconBackground.INFO,
    };
  }

  private getSingleRequestsNotificationItems(requester: User): AccessRequestsNotificationItems {
    this.adjustRejectButton(requester);
    this.adjustApproveButton(requester);

    const isGuestUser = requester.isAnonymous || this.aclService.isAnonymous(requester);
    if (isGuestUser) {
      this.adjustSingleGuestTitle(requester);
    } else {
      this.adjustSingleUserTitle(requester);
    }

    return {
      secondaryActionButton: this._rejectButton,
      primaryActionButton: this._approveButton,
      notificationTitle: isGuestUser
        ? this._singleStyleGuestNotificationTitle
        : this._singleStyleUserNotificationTitle,
      notificationIconConfig: isGuestUser
        ? this._singleStyleGuestNotificationIcon
        : this._singleStyleUserNotificationIcon,
    };
  }

  private adjustRejectButton(requester: User) {
    if (!this._rejectButton) {
      this._rejectButton = {
        text: this.translateService.instant('Reject'),
        icon: 'access_requests_reject_black',
        onClick: () => {
          this.rejectRequesterFromNotification(requester);
        },
        iconConfig: {
          type: IconTypes.svgIcon,
        },
        close: true,
      };
    } else {
      this._rejectButton.onClick = () => {
        this.rejectRequesterFromNotification(requester);
      };
      this._rejectButton.close = true;
    }
  }

  private adjustApproveButton(requester: User) {
    if (!this._approveButton) {
      this._approveButton = {
        text: this.acceptButtontext,
        icon: 'access_requests_approve_white',
        onClick: () => {
          this.approveRequesterFromNotification(requester);
        },
        iconConfig: {
          type: IconTypes.svgIcon,
        },
        close: true,
      };
    } else {
      this._approveButton.onClick = () => {
        this.approveRequesterFromNotification(requester);
      };
      this._approveButton.close = true;
    }
  }

  private adjustSingleGuestTitle(requester: User) {
    this._singleStyleGuestNotificationIcon = {
      assetName: 'access_requests_guest_icon',
      type: NotificationIconTypes.SVG_ICON,
      width: 18,
      height: 18,
      background: IconBackground.INFO,
    };

    this._singleStyleGuestNotificationTitle = this.getSingleNotificationTitle(requester);
  }

  private adjustSingleUserTitle(requester: User) {
    this._singleStyleUserNotificationTitle = this.getSingleNotificationTitle(requester);
    this._singleStyleUserNotificationIcon = {
      size: 32,
      type: NotificationIconTypes.USER,
      user: requester,
    };
  }

  private getSingleNotificationTitle(requester: User) {
    const requesterName =
      requester.name || requester.email || this.translateService.instant('Unknown User');
    return `${requesterName} ${this.singleRequestNotificationText}`;
  }

  private approveAll() {
    this._approveAll$.next();
  }

  public get approveAll$() {
    return this._approveAll$.asObservable();
  }

  public dismissRequestsNotification() {
    this.notificationToasterService.dismissNotificationsByCode([this.notificationCode]);
    this.isNotificationActive = false;
  }

  protected approveRequesterFromNotification(requester: User) {
    this._requesterApproved$.next(requester);
  }

  protected rejectRequesterFromNotification(requester: User) {
    this._requesterRejected$.next(requester);
  }

  protected goToAccessRequestsSection() {
    this.sessionSharedDataService.changeParticipantsManagerSection.next(Section.QUEUE);
    if (
      this.sessionSharedDataService.changeRightPanelView.value !== PanelView.participantsManager
    ) {
      this.sessionSharedDataService.changeRightPanelView.next(PanelView.participantsManager);
    }
  }

  public warning(msg?: string, title?: string) {
    if (!msg && !title) {
      return;
    }

    const newTitle = (title ?? msg) as string;
    this.showBasicWarning(this.warningCode, !!(msg && title), newTitle, msg);
  }

  public showSomethingWentWrongNotification() {
    if (!this._somethnigWentWrongNotificationData) {
      this.buildSomethingWentWrongNotification();
    }

    this.notificationToasterService.showNotification(this._somethnigWentWrongNotificationData);
  }

  private buildSomethingWentWrongNotification() {
    const notificationIcon: NotificationIconConfig = {
      assetName: this.defaultNotificationIconName,
      width: 18,
      height: 18,
      type: NotificationIconTypes.SVG_ICON,
      background: IconBackground.INFO,
    };

    const title = this.translateService.instant('Something went wrong');
    const description = this.translateService.instant(
      'Please refresh and try again, or contact support.',
    );

    const refreshButton = {
      text: this.translateService.instant('Refresh'),
      onClick: () => {
        this.refresh();
      },
      close: true,
    };

    const supportButton = {
      text: this.translateService.instant('Contact Support'),
      onClick: () => {
        this.contactSupport();
      },
      close: true,
      icon: 'help_outline',
      iconConfig: {
        type: IconTypes.matIcon,
      },
    };

    this._somethnigWentWrongNotificationData = new V2NotificationDataBuilder(this.warningCode)
      .type(NotificationType.WARNING)
      .style(ToasterPopupStyle.WARN)
      .icon(notificationIcon)
      .title(title)
      .description(description)
      .actionButtonsConfig({
        primary: refreshButton,
        secondary: supportButton,
      })
      .showProgressBar(false)
      .dismissable(true)
      .timeOut(15)
      .extendedTimeout(15)
      .priority(600)
      .build();
  }

  private refresh() {
    location.reload();
  }

  private contactSupport() {
    this.customerSupportService.openSupport();
  }

  private showBasicWarning(code: string, withMsg: boolean, title: string, msg?: string) {
    const notificationIcon: NotificationIconConfig = {
      assetName: this.defaultNotificationIconName,
      width: 18,
      height: 18,
      type: NotificationIconTypes.SVG_ICON,
      background: IconBackground.INFO,
    };
    const notificationData = new V2NotificationDataBuilder(code)
      .type(NotificationType.WARNING)
      .style(ToasterPopupStyle.WARN)
      .icon(notificationIcon)
      .title(title)
      .showProgressBar(false)
      .dismissable(true)
      .timeOut(5)
      .extendedTimeout(5)
      .priority(600);

    if (withMsg && msg) {
      notificationData.description(msg);
    }

    this.notificationToasterService.showNotification(notificationData.build());
  }

  public get requesterApproved$() {
    return this._requesterApproved$.asObservable();
  }

  public get requesterRejected$() {
    return this._requesterRejected$.asObservable();
  }

  public startShowingNotifications() {
    this._startShowingNotifications = true;
  }

  public stopShowingNotifications() {
    this._startShowingNotifications = false;
    this._lastRequestsNum = -1;
    this._lastRequesterID = '';
  }

  public dismissAllNotifications() {
    this.notificationToasterService.dismissAllNotifications();
  }
}

export interface AccessRequestsNotificationItems {
  notificationDescription?: string;
  notificationTitle: string;
  primaryActionButton: NotificationActionButton;
  secondaryActionButton: NotificationActionButton;
  notificationIconConfig: NotificationIconConfig;
}
